import { uniq } from 'es-toolkit';

import { ComponentDesign, JackGirderConnection, LayoutModel } from '@paragon/contract-library';

interface Vertex {
  guid: string;
  parentGuids: Set<string>;
  childGuids: Set<string>;
}

/**
 * Some of the design decisions of this class may seem strange or non-standard, such as the storage of both parent and
 *   child guids within a vertex itself, and the storage of vertexes within the graph as a map of guid to vertex.  This
 *   DAG implementation was created in order to enable two operations, and was optimized for them:
 *     1) Constructing a graph to describe jack girder connections between component designs.
 *     2) Counting the number of edges between a given vertex and a root.
 *
 * If you're looking for a general purpose directed acyclic graph, there are probably better options out there.
 */
export class DirectedAcyclicGraph {
  private vertices = new Map<string, Vertex>();
  private maxDistanceToRootVertexCache = new Map<string, number>();

  private constructor(nodes: Map<string, Vertex>) {
    this.vertices = nodes;
  }

  private get maxRecursions() {
    return this.vertices.size - 1;
  }

  getMaxDistanceToAnyRootVertex = (vertexGuid: string): number =>
    this.getMaxDistanceToAnyRootVertexRecursive(vertexGuid, this.maxRecursions);

  private getMaxDistanceToAnyRootVertexRecursive = (vertexGuid: string, recursionsBeforeError: number): number => {
    if (this.maxDistanceToRootVertexCache.has(vertexGuid)) return this.maxDistanceToRootVertexCache.get(vertexGuid)!;

    const vertex = this.vertices.get(vertexGuid);
    if (!vertex) throw new Error(`Invalid vertex guid: ${vertexGuid}`);

    if (vertex.parentGuids.size === 0) return 0;

    if (recursionsBeforeError === 0) throw new Error('Max recursion depth reached; there are cycles in the graph');

    return (
      1 +
      Math.max(
        ...Array.from(vertex.parentGuids).map((guid) =>
          this.getMaxDistanceToAnyRootVertexRecursive(guid, recursionsBeforeError - 1),
        ),
      )
    );
  };

  static fromComponentDesignsAndJackGirderConnections(
    componentDesigns: readonly ComponentDesign[] | readonly LayoutModel[],
    connections: readonly JackGirderConnection[],
  ): DirectedAcyclicGraph {
    const nodes = new Map<string, Vertex>();

    for (const componentDesign of componentDesigns) {
      nodes.set(componentDesign.guid, {
        guid: componentDesign.guid,
        parentGuids: new Set<string>(),
        childGuids: new Set<string>(),
      });
    }

    for (const { jackGuid, girderGuid } of connections) {
      if (!nodes.has(jackGuid)) {
        throw new Error(`Component ${jackGuid} does not exist.`);
      }

      if (!nodes.has(girderGuid)) {
        throw new Error(`Component ${girderGuid} does not exist.`);
      }

      nodes.get(girderGuid)!.childGuids.add(jackGuid);
      nodes.get(jackGuid)!.parentGuids.add(girderGuid);
    }

    return new DirectedAcyclicGraph(nodes);
  }

  getDescendantGuids = (guid: string) => {
    const vertex = this.vertices.get(guid);
    if (!vertex) throw new Error('Vertex not found.');

    let descendantGuids: string[] = [];
    let childVertices = this.getChildVertices(vertex);
    let iterationsBeforeError = this.maxRecursions;
    while (childVertices.length > 0 && iterationsBeforeError !== 0) {
      descendantGuids = descendantGuids.concat(childVertices.map((vertex) => vertex.guid));
      childVertices = childVertices.flatMap((childVertex) => this.getChildVertices(childVertex));
      iterationsBeforeError -= 1;
    }

    if (iterationsBeforeError >= 0) return uniq(descendantGuids);

    throw new Error('Maximum depth reached; there are cycles in the graph');
  };

  private getChildVertices = (vertex: Vertex) => {
    const childVertices: Vertex[] = [];
    for (const childGuid of vertex.childGuids) {
      const childVertex = this.vertices.get(childGuid);
      if (!childVertex) throw new Error('Child vertex not found.');
      childVertices.push(childVertex);
    }
    return childVertices;
  };
}
